import Vue from "vue";
import Router from "vue-router";
import store from "@/store"; // 引入 store

Vue.use(Router);

/**
 * Note: 路由配置项
 *
 * hidden: true                     // 当设置 true 的时候该路由不会再侧边栏出现 如401，login等页面，或者如一些编辑页面/edit/1
 * alwaysShow: true                 // 当你一个路由下面的 children 声明的路由大于1个时，自动会变成嵌套的模式--如组件页面
 *                                  // 只有一个时，会将那个子路由当做根路由显示在侧边栏--如引导页面
 *                                  // 若你想不管路由下面的 children 声明的个数都显示你的根路由
 *                                  // 你可以设置 alwaysShow: true，这样它就会忽略之前定义的规则，一直显示根路由
 * redirect: noRedirect             // 当设置 noRedirect 的时候该路由在面包屑导航中不可被点击
 * name:'router-name'               // 设定路由的名字，一定要填写不然使用<keep-alive>时会出现各种问题
 * query: '{"id": 1, "name": "ry"}' // 访问路由的默认传递参数
 * roles: ['admin', 'common']       // 访问路由的角色权限
 * permissions: ['a:a:a', 'b:b:b']  // 访问路由的菜单权限
 * meta : {
    noCache: true                   // 如果设置为true，则不会被 <keep-alive> 缓存(默认 false)
    title: 'title'                  // 设置该路由在侧边栏和面包屑中展示的名字
    icon: 'svg-name'                // 设置该路由的图标，对应路径src/assets/icons/svg
    breadcrumb: false               // 如果设置为false，则不会在breadcrumb面包屑中显示
    activeMenu: '/system/user'      // 当路由设置了该属性，则会高亮相对应的侧边栏。
  }
 */

// 公共路由
export const constantRoutes = [
  {
    path: "/login",
    component: (resolve) => require([`@/views/login`], resolve),
    hidden: true,
  },
  {
    path: "/bigscreen/designer",
    component: () => import("@/views/bigscreenDesigner/designer"),
    hidden: true,
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/bigscreen/viewer",
    component: () => import("@/views/bigscreenDesigner/viewer"),
    hidden: true,
    meta: {
      requireAuth: true,
    },
  },
  {
    path: "/",
    redirect: "/login",
    hidden: true,
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "*",
    redirect: "/login",
    hidden: true,
  },
];

// 防止连续点击多次路由报错
let routerPush = Router.prototype.push;
let routerReplace = Router.prototype.replace;
// push
Router.prototype.push = function push(location) {
  return routerPush.call(this, location).catch((err) => err);
};
// replace
Router.prototype.replace = function push(location) {
  return routerReplace.call(this, location).catch((err) => err);
};
const router = new Router({
  mode: "history", // 去掉url中的#
  scrollBehavior: () => ({ y: 0 }),
  routes: constantRoutes,
});

// ============ 路由守卫核心代码 ============
// 从 URL 中提取 token 的辅助函数
function getTokenFromUrl(to) {
  // 方式1: 从路径参数获取 (例如: /bigscreen/viewer/abc123)
  if (to.params && to.params.token) {
    return to.params.token;
  }
  // 方式2: 从查询参数获取 (例如: /bigscreen/viewer?token=abc123)
  if (to.query && to.query.token) {
    return to.query.token;
  }
  // 方式3: 从路径中匹配 (例如: /bigscreen/designer/abc123)
  const pathMatch = to.path.match(/\/(bigscreen|viewer|designer)\/([^/]+)/);
  if (pathMatch && pathMatch[2]) {
    return pathMatch[2];
  }

  return null;
}

// 全局前置守卫
router.beforeEach(async (to, from, next) => {
  // 1. 设置页面标题
  if (to.meta && to.meta.title) {
    document.title = to.meta.title;
  }

  // 2. 优先级: URL中的token > localStorage中的token
  let token = getTokenFromUrl(to);
  // console.log("路由跳转:", to);
  // 如果URL中没有token
  if (!token) {
    next({
      path: "/login",
      query: {
        redirect: to.fullPath,
        error: "no_requireAuth",
      },
    });
  }

  // 3. 判断是否需要登录验证
  const requireAuth = to.matched.some((record) => record.meta.requireAuth);

  // 不需要登录验证的路由
  if (!requireAuth) {
    next({
      path: "/login",
      query: {
        redirect: to.fullPath,
        error: "no_requireAuth",
      },
    });
    return;
  }

  // 需要登录验证的路由
  if (token) {
    try {
      // 检查是否已经验证过登录（避免重复请求）
      const isVerified =
        store.state.isLoginVerified &&
        store.state.token === token &&
        store.state.userInfo;

      // 如果已验证且token相同，直接放行
      if (isVerified) {
        // console.log("已验证，直接放行");
        next();
        return;
      }

      // 显示加载状态
      store.commit("SET_LOADING", true);
      // console.log("开始验证token:", token);

      // 调用登录验证接口
      const loginByToken = (await import("@/api/login/index")).loginByToken;
      const res = await loginByToken({
        loginToken: token,
        loginType: 1,
      });

      // console.log("登录验证结果:", res);

      if (res.code === 0) {
        // 登录验证成功
        store.commit("SET_TOKEN", token);
        store.commit("SET_USER", res.data || { token });
        store.commit("SET_LOGIN_VERIFIED", true);
        next();
      } else {
        // token验证失败
        console.warn("Token验证失败:", res.msg || res.message);
        clearAuthData();
        next({
          path: "/login",
          query: {
            redirect: to.fullPath,
            error: "token_invalid",
          },
        });
      }
    } catch (error) {
      console.error("路由守卫验证异常:", error);
      clearAuthData();
      next({
        path: "/login",
        query: {
          redirect: to.fullPath,
          error: "network_error",
        },
      });
    } finally {
      store.commit("SET_LOADING", false);
    }
  } else {
    // 没有token，跳转到登录页
    console.warn("未找到token，跳转到登录页");
    next({
      path: "/login",
      query: {
        redirect: to.fullPath,
        error: "no_token",
      },
    });
  }
});

// 清除认证数据
function clearAuthData() {
  store.commit("SET_TOKEN", null);
  store.commit("SET_USER", null);
  store.commit("SET_LOGIN_VERIFIED", false);
}

export default router;
